import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import FooterEN from '../../home/components/FooterEN';
import GlobalNavbarEN from '../../../components/feature/GlobalNavbarEN';

export default function LocationsPage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const locations = [
    {
      id: 'las-vegas-strip',
      title: 'Las Vegas Strip',
      subtitle: 'Las Vegas Strip',
      label: 'Most Popular',
      labelColor: 'bg-amber-500',
      description: 'Premium billboard locations along the world-famous Las Vegas Strip. Unmatched visibility with over 42 million annual visitors from around the globe.',
      stats: { traffic: '42M+', visibility: '24/7', roi: '320%' },
      image: 'https://public.readdy.ai/ai/img_res/00c363236a1328459176c09c6e3ea62a.jpg',
    },
    {
      id: 'i15-highway',
      title: 'Interstate 15',
      subtitle: 'Interstate 15',
      label: 'High Traffic',
      labelColor: 'bg-emerald-500',
      description: 'Strategic billboard placements along I-15, the primary corridor connecting Los Angeles and Las Vegas. High-impact exposure with over 180,000 daily vehicle impressions.',
      stats: { traffic: '180K/day', visibility: 'Highway', roi: '280%' },
      image: 'https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20photograph%20of%20Interstate%2015%20highway%20in%20Las%20Vegas%20from%20above%20showing%20multiple%20lanes%20of%20traffic%20flowing%20through%20the%20desert%20landscape%20with%20the%20famous%20Las%20Vegas%20Strip%20skyline%20visible%20in%20the%20background%20multiple%20large%20billboard%20advertisements%20prominently%20displayed%20along%20both%20sides%20of%20the%20highway%20illuminated%20billboards%20with%20advertising%20displays%20golden%20hour%20lighting%20professional%20drone%20photography%20perspective%20realistic%20urban%20infrastructure%20clear%20blue%20sky%20cinematic%20composition%20capturing%20the%20scale%20of%20the%20highway%20system%20with%20numerous%20billboards%20and%20surrounding%20Nevada%20desert%20terrain&width=800&height=600&seq=i15billboards003&orientation=landscape',
    },
    {
      id: 'airport-area',
      title: 'Airport Area',
      subtitle: 'Harry Reid International Airport',
      label: 'International Reach',
      labelColor: 'bg-sky-500',
      description: 'Premium billboard locations surrounding Harry Reid International Airport. Capture the attention of domestic and international travelers with 52 million annual passengers.',
      stats: { traffic: '52M/year', visibility: 'International', roi: '290%' },
      image: 'https://public.readdy.ai/ai/img_res/edited_0a225551fd039a5f674db06d77b206d7_d442473f.jpg',
    },
    {
      id: 'allegiant-stadium',
      title: 'Allegiant Stadium',
      subtitle: 'Allegiant Stadium',
      label: 'Event-Driven',
      labelColor: 'bg-rose-500',
      description: 'High-visibility billboard locations near Allegiant Stadium, home of the NFL Raiders. Reach up to 65,000 attendees during major sporting events and concerts.',
      stats: { traffic: '65K/event', visibility: 'NFL/Concerts', roi: '350%' },
      image: 'https://readdy.ai/api/search-image?query=Allegiant%20Stadium%20Las%20Vegas%20exterior%20front%20view%20showing%20the%20massive%20curved%20black%20dome%20architecture%20with%20prominent%20large%20colorful%20digital%20LED%20billboard%20advertisement%20display%20screen%20mounted%20on%20the%20stadium%20facade%20vibrant%20advertising%20content%20visible%20on%20electronic%20billboard%20evening%20golden%20hour%20lighting%20professional%20architectural%20photography%20perspective%20similar%20to%20ground%20level%20view%20capturing%20the%20impressive%20scale%20of%20both%20the%20stadium%20and%20the%20digital%20advertising%20display%20urban%20Las%20Vegas%20background%20with%20clear%20sky%20high%20quality%20realistic%20commercial%20photography&width=800&height=600&seq=stadiumfront007billboard&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 font-inter">
      <GlobalNavbarEN />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-1.5 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full mb-4">
              PREMIUM LOCATIONS
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Las Vegas Billboard Locations
              <span className="block text-2xl md:text-3xl font-medium text-amber-400 mt-2">
                High-Traffic Outdoor Advertising Corridors
              </span>
            </h1>
            <h2 className="text-gray-400 text-base max-w-2xl mx-auto">
              Premium digital billboard placements across<br />
              the Las Vegas Strip, I-15, Airport, and Allegiant Stadium
            </h2>
            <p className="text-gray-400 text-base max-w-2xl mx-auto mt-4">
              Explore strategic billboard advertising locations designed to maximize visibility, impressions, and measurable ROI throughout Las Vegas.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Location Cards Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {locations.map((location, index) => (
              <motion.div
                key={location.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-2xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 cursor-pointer"
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={location.image}
                    alt={location.title}
                    className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                      location.id === 'allegiant-stadium' ? 'object-center' : 'object-top'
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                  <span className={`absolute top-4 left-4 px-3 py-1 ${location.labelColor} text-white text-xs font-medium rounded-full`}>
                    {location.label}
                  </span>
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white/70 text-xs">{location.subtitle}</p>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{location.title}</h3>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{location.description}</p>
                  
                  {/* Stats */}
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100">
                    <div className="text-center">
                      <p className="text-amber-500 font-bold text-sm">{location.stats.traffic}</p>
                      <p className="text-gray-400 text-xs">Traffic</p>
                    </div>
                    <div className="text-center">
                      <p className="text-amber-500 font-bold text-sm">{location.stats.visibility}</p>
                      <p className="text-gray-400 text-xs">Visibility</p>
                    </div>
                    <div className="text-center">
                      <p className="text-amber-500 font-bold text-sm">{location.stats.roi}</p>
                      <p className="text-gray-400 text-xs">Avg ROI</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-6 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-3xl p-10 border border-amber-500/30"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">
              Find Your Perfect Location
            </h2>
            <p className="text-gray-400 text-sm mb-6">
              Our team will recommend optimal billboard placements tailored to your campaign objectives and target audience
            </p>
            <a
              href="/en/advertise#inquiry-form"
              className="inline-flex items-center gap-2 px-8 py-3 bg-amber-500 hover:bg-amber-600 text-white font-medium rounded-full transition-all whitespace-nowrap cursor-pointer"
            >
              Schedule Free Consultation
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </motion.div>
        </div>
      </section>

      <FooterEN />
    </div>
  );
}
